/* eslint-disable max-params */
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { useStore } from 'effector-react';
import { columnsStyle, containerStyle, rowsStyle } from 'styles/global.styles';

import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowForwardOutlined } from '@mui/icons-material';
import { Box, Button, Drawer, Grid, Stack, Typography, useTheme } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

import TextInput from 'components/form/textInput';
import ConfirmModal from 'components/ui/confirmModal/ConfirmModal';
import ContentPage from 'components/ui/content';
import CustomPagination from 'components/ui/pagination/Pagination';
import SkeletonTableLoading from 'components/ui/skeletonTable/SkeletonTable';

import { createCompaignUseCase } from 'useCases/compaignManagement/createCompaignUseCase';
import { getCompaignManagementUseCase } from 'useCases/compaignManagement/getCompaignManagementUseCase';

import { compaignManagementEvent } from 'stores/CompaignManagement/CompaignManagementEvents';
import compaignManagementStore from 'stores/CompaignManagement/CompaignManagementStore';

import { CompaignManagementValidators } from './CompaignManagementValidators';

export default function CompaignsManagementPage(): JSX.Element {
  const theme = useTheme();
  const { list, isLoading, isOpenForm, isLoadingForm, pageNow, pageSize, isOpenModalSubmit } =
    useStore(compaignManagementStore);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm({
    resolver: yupResolver(CompaignManagementValidators),
    mode: 'all',
  });

  const closeForm = () => compaignManagementEvent({ isOpenForm: false });
  const openForm = () => compaignManagementEvent({ isOpenForm: true });

  useEffect(() => {
    getCompaignManagementUseCase.execute();
  }, [pageNow, pageSize]);

  return (
    <>
      <Box sx={{ ...containerStyle, flexDirection: 'column' }}>
        <ContentPage title="Gestão de Campanhas">
          <Grid
            container
            alignItems="flex-end"
            alignContent="flex-end"
            justifyContent="flex-end"
            mt={5}
            mb={2}
            mr={1}
          >
            <Stack gap={2} flexDirection="row">
              <Box>
                <Button
                  variant="contained"
                  onClick={() => {
                    reset({});
                    openForm();
                  }}
                >
                  CRIAR CAMPANHA
                </Button>
              </Box>
            </Stack>
          </Grid>
          <TableContainer
            component={Paper}
            sx={{ boxShadow: 'rgba(50, 50, 93, 0.25) 0px 0px 5px 0px' }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="simple table">
              <TableHead>
                <TableRow>
                  <TableCell sx={columnsStyle} align="left">
                    Descrição
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <SkeletonTableLoading />
                ) : (
                  <>
                    {list?.data?.campaigns?.length === 0 && (
                      <Box p={3} minWidth="300px">
                        <Typography variant="caption" textAlign="center">
                          Nenhum registro encontrado...
                        </Typography>
                      </Box>
                    )}
                    {list?.data?.campaigns?.map(row => (
                      <TableRow
                        key={row.id}
                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                      >
                        <TableCell align="left" component="th" scope="row">
                          <Typography sx={rowsStyle}>{row?.name}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            total={list?.data?.totalPages || 1}
            page={list?.data?.currentPage || 1}
            setPage={v => compaignManagementEvent({ pageNow: v })}
            isLoading={isLoading}
          />
        </ContentPage>
      </Box>
      <Drawer anchor="right" open={isOpenForm} onClose={closeForm}>
        <Box
          sx={{
            [theme.breakpoints.between('sm', 'xs')]: {
              width: 480,
              maxWidth: 480,
            },
            [theme.breakpoints.up('md')]: {
              width: 580,
              maxWidth: 580,
            },
            minHeight: '100%',
            borderRadius: '10px 0 0 10px !important',
            backgroundColor: '#FFF',
          }}
        >
          <Stack
            px={4}
            py={4}
            direction="column"
            justifyContent="space-between"
            flex={1}
            sx={{
              height: '100%',
            }}
          >
            <div>
              <Typography fontSize="26px" fontWeight="bold">
                Criar campanha
              </Typography>
              <Box mt={8}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field: { onChange, value, name } }) => {
                    return (
                      <TextInput
                        label="Insira o nome da campanha"
                        name={name}
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    );
                  }}
                />
              </Box>
            </div>

            <Stack direction="row" justifyContent="flex-end">
              <Button
                variant="contained"
                sx={{ width: 150 }}
                disabled={!isValid}
                onClick={() => compaignManagementEvent({ isOpenModalSubmit: true })}
                endIcon={<ArrowForwardOutlined color="inherit" />}
              >
                CRIAR
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Drawer>

      <ConfirmModal
        open={isOpenModalSubmit}
        setOpen={v => compaignManagementEvent({ isOpenModalSubmit: v })}
        text="Tem certeza que deseja criar uma nova campanha?"
        onConfirm={handleSubmit(createCompaignUseCase.execute)}
        isLoading={isLoadingForm}
        title="Atenção"
        declineText="CANCELAR"
        confirmText="CONFIRMAR"
      />
    </>
  );
}
